import React, { useEffect, useState } from 'react';
import { collection, deleteDoc, doc, getDocs, query } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuthContext } from '../../context/AuthContext';
import PlayerForm from './PlayerForm';
import { Player } from '../../types/Player';
import { positionOptions } from './utils/playerUtils';
import ConfirmModal from '../ConfirmModal';
import { Button } from 'flowbite-react';
import { usePageTitle } from '../../hooks/usePageTitle';

const PlayerList: React.FC = () => {
  usePageTitle('Players | From the Pitchside');
  const { user } = useAuthContext();

  const [players, setPlayers] = useState<Player[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [playerToDelete, setPlayerToDelete] = useState<Player | null>(null);

  const numberOfPlayers = players.length;

  useEffect(() => {
    if (!user?.teamId || !user?.clubId) return;

    const fetchPlayers = async () => {
      const q = query(collection(db, 'clubs', user.clubId, 'teams', user.teamId, 'players'));

      const snapshot = await getDocs(q);
      const playerList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Player[];

      setPlayers(
        playerList
          .sort((a, b) => {
            const firstNameCompare = a.firstName.localeCompare(b.firstName);
            if (firstNameCompare !== 0) return firstNameCompare;
            return a.lastName.localeCompare(b.lastName);
          })
          .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)) // Optional: keep pinned players on top
      );
    };

    fetchPlayers();
  }, [user]);

  const handleEdit = (player: Player) => {
    setEditingPlayer(player);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setShowForm(true);
  };

  const handleSave = (newPlayer: Player) => {
    setPlayers(prev =>
      prev.some(p => p.id === newPlayer.id)
        ? prev.map(p => (p.id === newPlayer.id ? newPlayer : p))
        : [...prev, newPlayer]
    );
    setShowForm(false);
    setEditingPlayer(null);
  };

  const confirmDelete = (player: Player) => {
    setPlayerToDelete(player);
  };

  const handleConfirmDelete = async () => {
    if (!user?.teamId || !playerToDelete?.id) return;

    await deleteDoc(
      doc(db, 'clubs', user.clubId, 'teams', user.teamId, 'players', playerToDelete.id)
    );
    setPlayers(prev => prev.filter(p => p.id !== playerToDelete.id));
    setPlayerToDelete(null);
  };

  return (
    <div className='p-4 text-darkblue'>
      <div className='flex justify-between items-center mb-4'>
        <div>
          <h2 className='text-2xl font-bold mb-4'>Player List</h2>
          <p>
            You currently have{' '}
            <span className='font-bold'>{numberOfPlayers}</span> players in your
            squad.
          </p>
        </div>
        <Button
          pill
          onClick={() => {
            setEditingPlayer(null);
            setShowForm(true);
          }}
          className='bg-lightblue text-white px-4 py-2'
        >
          Add Player
        </Button>
      </div>

      {showForm && (
        <PlayerForm
          player={editingPlayer}
          onSave={handleSave}
          onCancel={() => {
            setEditingPlayer(null);
            setShowForm(false);
          }}
        />
      )}

      <div className='grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'>
        {players.map(player => (
          <div
            key={player.id}
            className='bg-white shadow rounded p-3 flex flex-col items-center text-center'
          >
            {/* <img
              src={player.photoUrl || playerPlaceholder}
              alt={`${player.firstName} ${player.lastName}`}
              className='w-20 h-20 rounded-md object-cover mb-2 border cursor-pointer'
              onClick={() => handleEdit(player)}
            /> */}
            <p
              className='font-bold cursor-pointer'
              onClick={() => handleEdit(player)}
            >
              {player.firstName} {player.lastName}
            </p>
            <p className='text-2xl font-bold bg-lightblue text-darkblue rounded-full w-12 h-12 flex justify-center items-center'>
              {player.shirtNumber}
            </p>
            <div className='text-sm mt-1 whitespace-pre-wrap'>
              <p className='text-sm text-gray-600 whitespace-pre-line'>
                {player.positions
                  .map(
                    pos =>
                      positionOptions.find(opt => opt.value === pos)?.label ||
                      pos
                  )
                  .join('\n')}
              </p>
            </div>
            <div className='flex gap-2 mt-3'>
              <button
                className='text-sm px-2 py-1 bg-darkblue text-white rounded'
                onClick={() => handleEdit(player)}
              >
                Edit
              </button>
              <button
                className='text-sm px-2 py-1 bg-red-500 text-white rounded'
                onClick={() => confirmDelete(player)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {playerToDelete && (
        <ConfirmModal
          title='Delete Player'
          message={`Are you sure you want to delete ${playerToDelete.firstName} ${playerToDelete.lastName}?`}
          onCancel={() => setPlayerToDelete(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
};

export default PlayerList;
